import React, { useRef, useState } from "react";
import { RCCButton, RCCColorPicker, RCCGrid, RCCHookToast, RCCIcon, RCCRefModalProps, RCCServicesApi, RCCTooltip,RCCRefLovProps } from "react-common-components";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { ITarget } from "src/pages/Config/Metrics/interface";
import LovMetrics from "src/components/Lov/Metrics";
import stringToNumber from "src/utils/stringToNumber";
import { v4 } from "uuid";
import { standardErrorMessages } from "src/utils/errors";
import { css } from "styled-components";
import returnColor from "src/utils/returnColor";
import InputNumber from "../InputNumber";
import { IComponentRanges } from "./interface";
import Modal from "../Modal";
import Title from "../Title";

const MetricsRanges: React.FC<IComponentRanges> = ({
	names,
	source,
	control,
	errors,
	clearErrors,
	append,
	remove,
	setValue,
	useMetricLov,
	useMetricDefault = true,
	metricId,
	titleText,
	disabled
}) => {

	const {confirm} = useConfirmModal();
	const {addToast} = RCCHookToast();
	const { currentLanguage, translate} = useTranslation();

	const defaultColors = ["#96281B", "#D35400", "#F5AB35", "#3A539B", "#52B3D9", "#019875", "#36D7B7", "#6C7A89", "#D5DADC"];

	const [temporaryMetric,setTemporaryMetric] = useState<string>();
	const [oldMetric,setOldMetric] = useState(metricId);
	const modalMetrics = useRef<RCCRefModalProps>(null);
	const lovRef = useRef<RCCRefLovProps>(null);

	const addRange = () => {
		clearErrors(names.table);
		const newRange = {...source[source.length-1]};
		newRange.id = v4();
		newRange[names.idName] = 0;
		const endValue = typeof newRange[names.endValueName] !== "number" ? stringToNumber(newRange[names.endValueName], currentLanguage) : newRange[names.endValueName];
		newRange[names.initValueName] = (endValue||0) + 0.01;
		newRange[names.endValueName] =  (endValue||0) + 20;
		newRange[names.colorName]=defaultColors[source.length + 1 > defaultColors.length ? defaultColors.length - 1 : source.length];
		append(newRange);
	};

	const deleteRange = (index:number) => {
		remove(index);
	};
	
	const requestRanges = async(metricIdRequest:string) => {

		if(!metricIdRequest) {
			setValue(names.table,[]);
			return;
		}

		const dataController = await RCCServicesApi.getRequest({
			method: "Metrics/ListRanges",
			urlParameters: {
				taa006Id:metricIdRequest
			}
		});

		const newArray = dataController.data.map((range:ITarget) => {
			const newObj = {} as any;
			newObj.id = v4();
			newObj[names.initValueName] = range.taa007Valorini;
			newObj[names.endValueName] =  range.taa007Valorfim;
			newObj[names.colorName]=range.taa007Cor;
			return newObj;
		});
		clearErrors(names.table);
		if(setValue && (typeof setValue ==="function")){
			setValue(names.table,newArray);
		}
			
	};

	const defaultMetricsRange = () => {
		const newArray = [];

		const m1 = {} as any;
		m1.id = v4();
		m1[names.initValueName] = 0;
		m1[names.endValueName] =  80.99;
		m1[names.colorName]= "#D91E18";
		newArray.push(m1);

		const m2 = {} as any;
		m2.id = v4();
		m2[names.initValueName] = 81;
		m2[names.endValueName] =  99.99;
		m2[names.colorName]= "#F5AB35";
		newArray.push(m2);

		const m3 = {} as any;
		m3.id = v4();
		m3[names.initValueName] = 100;
		m3[names.endValueName] =  9999;
		m3[names.colorName]= "#019875";
		newArray.push(m3);

		clearErrors(names.table);
		if(setValue && (typeof setValue ==="function")){
			setValue(names.table,newArray);
		}
	};

	return(
		<RCCGrid container>
			{useMetricLov && 
			<RCCGrid container item>	
				<Title 
					size="xs"
					color="grey"
				>
					{titleText}
				</Title>	
				<RCCGrid container wrap="nowrap" alignItems="center">
					<RCCGrid container item>
						<LovMetrics
							name={names.metricIdName}
							label={translate("Métrica padrão")}
							control={control}
							isClearable
							error={errors[names.metricIdName||""] && errors?.[names.metricIdName||""]}
							defaultValue={metricId}
							onChangeCapture={(newValue:any) => {
								if(newValue?.value !== oldMetric){
									setTemporaryMetric(newValue?.value);
									modalMetrics.current?.openModal();
								}
							}}
							ref={lovRef}
							disabled={disabled}
						/>
					</RCCGrid>
					{oldMetric &&
						<RCCTooltip
							text={translate("Restaurar faixas")}
						>
							<RCCButton
								icon={{name:"refresh"}}
								size="sm"
								styleInline="margin-bottom:-15px;"
								onClick={
									() => confirm(
										translate("Restaurar faixas"),
										translate("Ao restaurar os valores padrões você irá perder todas as configurações atuais, tem certeza?"),
										() => {
											const value = lovRef.current?.getValue() as Array<{value:string,label:string}>;
											if(value[0]){
												requestRanges(value[0].value);
											}
										},
										(err) => {
											standardErrorMessages(err, translate("Erro ao restaurar faixas"), addToast);
										}
									)
								}
								disabled={disabled}
							/>
						</RCCTooltip>
					}
				</RCCGrid>
			</RCCGrid>
			}
			<RCCGrid container justifyContent="space-between" alignContent="flex-end" wrap="nowrap">
				{errors[names.table] &&
					<RCCGrid container item alignItems="center">	
						<RCCIcon name="errorOutline" size={19} color="var(--red)"/>
						<Title color="red" size="sm">{errors[names.table]?.message}</Title>
					</RCCGrid>
				}
				<RCCGrid container item justifyContent="flex-end">
					<RCCTooltip
						text={translate("Adicionar Faixa")}
						color="purple"
						position="left">
						<RCCButton
							icon={{name: "plus"}}
							color="purple"
							onClick={() => {addRange();}}
							layout="isRounded"
							size="sm"
							disabled={disabled}
						/>	
					</RCCTooltip>
				</RCCGrid>
			</RCCGrid>
			<hr/>
			{source.map((range,index) => (
				<RCCGrid 
					key={range.id}
					container>
					<RCCGrid
						container
						item
						wrap="nowrap"
						spacing={3}
						alignContent="center"
						justifyContent="center">
						<RCCGrid  container item>
							<InputNumber
								control={control}
								label={translate("Início")}
								name={`${names.table}.${index}.${names.initValueName}`}
								allowNegative
								defaultValue={range[names.initValueName]}
								required
								maxLength={25}
								error={errors[names.table] && errors?.[names.table][index]?.[names.initValueName]}
								disabled={disabled}
							/>
						</RCCGrid>
						<RCCGrid container item>
							<InputNumber
								control={control}
								label={translate("Fim")}
								name={`${names.table}.${index}.${names.endValueName}`}
								allowNegative
								maxLength={25}
								defaultValue={range[names.endValueName]}
								required
								error={errors[names.table] && errors?.[names.table][index]?.[names.endValueName]}
								disabled={disabled}
							/>
						</RCCGrid>
						<RCCGrid 
							container
							item
							justifyContent="space-between"
							alignContent="flex-end">
							<RCCColorPicker
								name={`${names.table}.${index}.${names.colorName}`}
								control={control}
								label={translate("Cor")}
								labelPosition="start"
								colorPickerProps ={{color:returnColor(range[names.colorName])}}
								defaultValue = {returnColor(range[names.colorName])}
							/>
							<RCCTooltip
								text={translate("Excluir Faixa")}
								color="red"
								position="left"
							>
								<RCCButton
									icon={{name: "trash"}}
									color="red"
									onClick={() => {deleteRange(index);}}
									layout="isRounded"
									size="sm"
									disabled={disabled}
								/>	
							</RCCTooltip>
						</RCCGrid>
					</RCCGrid>
					
				</RCCGrid>
			))}
			{useMetricLov && 
				<Modal
					ref={modalMetrics}
					header={{
						title:translate("Confirmação")
					}}
					withoutForm
					styleInline={css`
					width: 50vw;
					min-width: 300px;
					height: 180px;
					position: absolute;
					top: 60px;
				`}
					footer={{
						customModalFooter: <>
							<RCCButton type="button" color="green" 
								onClick={() => { 
									requestRanges(temporaryMetric||""); 
									setOldMetric(temporaryMetric);
									modalMetrics.current?.closeModal();

									if(useMetricDefault && !temporaryMetric){
										defaultMetricsRange();
									}
								}}
							>
								{translate("Sim, alterar todas as faixas")}
							</RCCButton>
							<RCCButton 
								type="button" 
								color="yellow"
								onClick={() => {
									setOldMetric(temporaryMetric);
									modalMetrics.current?.closeModal();
								}}>
								{translate("Não, alterar apenas a métrica padrão")}
							</RCCButton>
							<RCCButton 
								type="button" 
								onClick={() => {
									if(!oldMetric){
										lovRef.current?.clearValue();
									}else{
										lovRef.current?.setValue(oldMetric);
									}
									modalMetrics.current?.closeModal();}
								}>
								{translate("Não, voltar ao valor anterior")}
							</RCCButton>
						</>
					}}
				>
					{translate("Ao restaurar os valores padrões você irá perder todas as configurações atuais, tem certeza?")}
				</Modal>
			}
		</RCCGrid>
	);
};

export default MetricsRanges;
